/**
 * PATH: packages/email-hub/src/feature/components/HomeOrgBadge.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENT-HOMEORGBADGE-001
 * PURPOSE: Show the email's home organisation in the WHERE lane.
 * WHY: Filing Integrity P2 — operators must see which org the email
 *      belongs to before allocating; mismatch with the shell tenant
 *      is highlighted so re-home is discoverable.
 */
import type { ReactElement } from 'react'

import { cn } from '../../_internal/lib/utils'
import { labelFamilyTitle } from '../lib/labelFamily'

export function HomeOrgBadge({
  organisationId,
  organisationName,
  shellOrgId,
  className,
}: {
  organisationId: number | null | undefined
  organisationName: string | null | undefined
  /** Active Email Hub / shell tenant org id (may differ from home). */
  shellOrgId?: number | null
  className?: string
}): ReactElement | null {
  if (organisationId == null && !organisationName) return null
  const label = organisationName?.trim() || `Org ${organisationId}`
  const mismatch =
    shellOrgId != null &&
    organisationId != null &&
    shellOrgId !== organisationId

  return (
    <span
      data-testid="home-org-badge"
      data-org-id={organisationId ?? undefined}
      data-mismatch={mismatch ? '1' : '0'}
      title={
        mismatch
          ? `${labelFamilyTitle('home_org')}. Shell tenant differs — use Move to organisation to re-home before allocating to this shell's boards.`
          : labelFamilyTitle('home_org')
      }
      className={cn(
        'inline-flex max-w-[10rem] items-center truncate rounded px-1.5 py-0.5 text-[10px] font-medium ring-1',
        mismatch
          ? 'bg-amber-50 text-amber-900 ring-amber-300 dark:bg-amber-950/40 dark:text-amber-100 dark:ring-amber-700'
          : 'bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-600',
        className,
      )}
    >
      {label}
    </span>
  )
}

export default HomeOrgBadge
